import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  useTheme,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  ListItem,
  Chip,
  Stack,
  Avatar,
  List,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  CheckCircle,
  TrendingUp,
  Business,
  Park,
  PrecisionManufacturing,
  AutoAwesome,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

interface PortfolioItem {
  id: string;
  title: string;
  description: string;
  image: string;
  category: string;
  client: string;
  year: string;
  impact: string;
  achievements: string[];
  technologies: string[];
  images?: string[];
}

const portfolioItems: PortfolioItem[] = [
  {
    id: '1',
    title: 'Luxury Hotel Furniture Collection',
    description: 'Custom furniture manufacturing for a 5-star hotel chain across East Africa',
    image: '/assets/portfolio/luxury-hotel-furniture.jpg',
    category: 'Furniture Manufacturing',
    client: 'Serena Hotels',
    year: '2024',
    impact: 'Delivered 500+ premium furniture pieces',
    achievements: [
      'Custom design and manufacturing of 500+ furniture pieces',
      'Integration of smart technology in 200+ units',
      'Achieved 99.8% client satisfaction rate',
      'Completed project 2 weeks ahead of schedule',
    ],
    technologies: ['Smart Furniture', 'Custom Design', 'Premium Materials', 'IoT Integration'],
  },
  {
    id: '2',
    title: 'Sustainable Textile Production Line',
    description: 'Eco-friendly textile manufacturing facility for fashion brands',
    image: '/assets/portfolio/sustainable-textiles.jpg',
    category: 'Textile Manufacturing',
    client: 'EcoFashion Kenya',
    year: '2024',
    impact: 'Produced 50,000+ sustainable fabric units',
    achievements: [
      'Reduced water consumption by 60%',
      'Achieved 100% organic material sourcing',
      'Created 150 new jobs in local communities',
      'Obtained international sustainability certifications',
    ],
    technologies: ['Organic Materials', 'Water Conservation', 'Digital Printing', 'Quality Control'],
  },
  {
    id: '3',
    title: 'Automotive Parts Manufacturing',
    description: 'Precision metalworks for automotive industry components',
    image: '/assets/portfolio/automotive-parts.jpg',
    category: 'Metalworks',
    client: 'Toyota Kenya',
    year: '2023',
    impact: 'Manufactured 10,000+ precision parts',
    achievements: [
      'Achieved 0.1% defect rate (industry standard: 2%)',
      'Reduced production time by 40%',
      'Implemented just-in-time delivery system',
      'Received Toyota Quality Excellence Award',
    ],
    technologies: ['CNC Machining', 'Laser Cutting', 'Quality Assurance', 'Lean Manufacturing'],
  },
  {
    id: '4',
    title: 'Smart Office Furniture System',
    description: 'Innovative office furniture with integrated technology',
    image: '/assets/portfolio/smart-office.jpg',
    category: 'Smart Furniture',
    client: 'TechHub Africa',
    year: '2023',
    impact: 'Transformed 50+ office spaces',
    achievements: [
      'Integrated IoT sensors in 300+ furniture pieces',
      'Reduced energy consumption by 35%',
      'Improved employee productivity by 25%',
      'Won Innovation Award at Africa Tech Summit',
    ],
    technologies: ['IoT Integration', 'Ergonomic Design', 'Smart Sensors', 'Mobile App Control'],
  },
  {
    id: '5',
    title: 'Medical Equipment Manufacturing',
    description: 'Precision manufacturing of medical devices and equipment',
    image: '/assets/portfolio/medical-equipment.jpg',
    category: 'Precision Manufacturing',
    client: 'MedTech Solutions',
    year: '2023',
    impact: 'Supplied 200+ healthcare facilities',
    achievements: [
      'Achieved ISO 13485 medical device certification',
      'Manufactured 5,000+ medical devices',
      'Zero safety incidents in production',
      'Reduced healthcare costs by 30%',
    ],
    technologies: ['Medical Grade Materials', 'Sterilization Processes', 'Quality Control', 'Regulatory Compliance'],
  },
  {
    id: '6',
    title: 'Renewable Energy Components',
    description: 'Manufacturing of solar panel components and wind turbine parts',
    image: '/assets/portfolio/renewable-energy.jpg',
    category: 'Energy Manufacturing',
    client: 'GreenPower Africa',
    year: '2022',
    impact: 'Supported 100+ renewable energy projects',
    achievements: [
      'Manufactured components for 50MW solar capacity',
      'Reduced carbon footprint by 45%',
      'Created 200+ green jobs',
      'Achieved 99.5% component reliability',
    ],
    technologies: ['Solar Technology', 'Wind Energy', 'Sustainable Materials', 'Energy Efficiency'],
  },
];

const categories = [
  { name: 'All Projects', value: 'all', icon: <Business /> },
  { name: 'Furniture', value: 'Furniture Manufacturing', icon: <Business /> },
  { name: 'Textiles', value: 'Textile Manufacturing', icon: <Business /> },
  { name: 'Metalworks', value: 'Metalworks', icon: <PrecisionManufacturing /> },
  { name: 'Smart Furniture', value: 'Smart Furniture', icon: <AutoAwesome /> },
  { name: 'Precision', value: 'Precision Manufacturing', icon: <PrecisionManufacturing /> },
  { name: 'Energy', value: 'Energy Manufacturing', icon: <Park /> },
];

export const Portfolio: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('all');

  const filteredItems = selectedCategory === 'all' 
    ? portfolioItems 
    : portfolioItems.filter(item => item.category === selectedCategory);

  return (
    <Box sx={{
      minHeight: '100vh',
      bgcolor: 'background.default',
      pt: 12,
      pb: 16,
    }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1b4d89 0%, #0f3460 100%)',
          color: 'white',
          py: 12,
          mb: 8,
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h1"
            component="h1"
            gutterBottom
            sx={{
              background: 'linear-gradient(135deg, #ffffff 0%, #e0e7ff 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 800,
              mb: 4,
              textAlign: 'center',
            }}
          >
            Monio Manufacturing Portfolio
          </Typography>
          <Typography variant="h5" sx={{ mb: 4, opacity: 0.9, textAlign: 'center' }}>
            Showcasing our manufacturing excellence and client success stories
          </Typography>
          <Typography variant="body1" sx={{ maxWidth: '800px', mx: 'auto', opacity: 0.8, textAlign: 'center' }}>
            Explore our diverse portfolio of manufacturing projects, from luxury furniture to precision engineering, 
            demonstrating our commitment to quality, innovation, and client satisfaction.
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg">
        {/* Category Filter */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 4, textAlign: 'center' }}>
            Our Projects
          </Typography>
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            spacing={2} 
            flexWrap="wrap"
            justifyContent="center"
            sx={{ mb: 6 }}
          >
            {categories.map((category) => (
              <Chip
                key={category.value}
                label={category.name}
                onClick={() => setSelectedCategory(category.value)}
                icon={category.icon}
                sx={{
                  background: selectedCategory === category.value 
                    ? 'linear-gradient(135deg, #1b4d89 0%, #0f3460 100%)'
                    : 'rgba(255, 255, 255, 0.1)',
                  color: selectedCategory === category.value ? 'white' : 'text.primary',
                  border: selectedCategory === category.value 
                    ? 'none' 
                    : '1px solid rgba(99, 102, 241, 0.2)',
                  '&:hover': {
                    background: selectedCategory === category.value 
                      ? 'linear-gradient(135deg, #0f3460 0%, #1b4d89 100%)'
                      : 'rgba(99, 102, 241, 0.1)',
                  },
                }}
              />
            ))}
          </Stack>
        </Box>

        {/* Portfolio Grid */}
        <Grid container spacing={4}>
          {filteredItems.map((item) => (
            <Grid item xs={12} md={6} lg={4} key={item.id}>
              <Card
                sx={{
                  height: '100%',
                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.98) 100%)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: 3,
                  boxShadow: '0px 20px 25px rgba(0, 0, 0, 0.1), 0px 10px 10px rgba(0, 0, 0, 0.04)',
                  transition: 'all 0.3s ease',
                  overflow: 'hidden',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0px 25px 50px rgba(0, 0, 0, 0.15)',
                  },
                }}
              >
                <Box
                  sx={{
                    height: 200,
                    background: `linear-gradient(135deg, #1b4d89 0%, #0f3460 100%)`,
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                />
                
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Chip 
                      label={item.category} 
                      size="small" 
                      sx={{ 
                        background: 'rgba(99, 102, 241, 0.1)',
                        color: '#6366f1',
                        fontWeight: 500,
                      }} 
                    />
                    <Typography variant="caption" sx={{ ml: 'auto', color: 'text.secondary' }}>
                      {item.year}
                    </Typography>
                  </Box>
                  
                  <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                    {item.title}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    {item.description}
                  </Typography>

                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                      Client: {item.client}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#10b981' }}>
                      Impact: {item.impact}
                    </Typography>
                  </Box>

                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 2 }}>
                    Key Achievements:
                  </Typography>
                  <List dense>
                    {item.achievements.slice(0, 3).map((achievement, index) => (
                      <ListItem key={index} sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 24 }}>
                          <CheckCircle sx={{ fontSize: 16, color: '#10b981' }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary={achievement} 
                          primaryTypographyProps={{ 
                            variant: 'body2',
                            sx: { fontSize: '0.875rem' }
                          }} 
                        />
                      </ListItem>
                    ))}
                  </List>

                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                      Technologies Used:
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {item.technologies.slice(0, 3).map((tech) => (
                        <Chip 
                          key={tech} 
                          label={tech} 
                          size="small" 
                          variant="outlined"
                          sx={{ 
                            borderColor: 'rgba(99, 102, 241, 0.3)',
                            color: '#6366f1',
                            mb: 1,
                          }} 
                        />
                      ))}
                    </Stack>
                  </Box>

                  <Button
                    variant="outlined"
                    fullWidth
                    sx={{
                      border: '2px solid #6366f1',
                      color: '#6366f1',
                      '&:hover': {
                        border: '2px solid #4f46e5',
                        background: 'rgba(99, 102, 241, 0.04)',
                      },
                    }}
                    onClick={() => navigate(`/portfolio/${item.id}`)}
                  >
                    View Project Details
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Statistics Section */}
        <Box sx={{ 
          mt: 8, 
          p: 6, 
          background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)', 
          borderRadius: 3,
          textAlign: 'center',
        }}>
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 700 }}>
            Our Impact in Numbers
          </Typography>
          <Grid container spacing={4} sx={{ mt: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="h2" sx={{ fontWeight: 800, color: '#6366f1', mb: 1 }}>
                500+
              </Typography>
              <Typography variant="h6" color="text.secondary">
                Projects Completed
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="h2" sx={{ fontWeight: 800, color: '#06b6d4', mb: 1 }}>
                200+
              </Typography>
              <Typography variant="h6" color="text.secondary">
                Happy Clients
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="h2" sx={{ fontWeight: 800, color: '#10b981', mb: 1 }}>
                99.8%
              </Typography>
              <Typography variant="h6" color="text.secondary">
                Client Satisfaction
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="h2" sx={{ fontWeight: 800, color: '#f59e0b', mb: 1 }}>
                5+
              </Typography>
              <Typography variant="h6" color="text.secondary">
                Years of Excellence
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Call to Action */}
        <Box sx={{ 
          mt: 8, 
          p: 6, 
          background: 'linear-gradient(135deg, #1b4d89 0%, #0f3460 100%)', 
          borderRadius: 3,
          textAlign: 'center',
          color: 'white',
        }}>
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 700 }}>
            Ready to Start Your Project?
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
            Let's discuss how Monio Manufacturing can bring your vision to life.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} justifyContent="center">
            <Button
              variant="contained"
              size="large"
              sx={{
                px: 6,
                py: 2,
                borderRadius: 3,
                background: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.3)',
                },
              }}
            >
              Start Your Project
            </Button>
            <Button
              variant="outlined"
              size="large"
              sx={{
                px: 6,
                py: 2,
                borderRadius: 3,
                border: '2px solid rgba(255, 255, 255, 0.5)',
                color: 'white',
                '&:hover': {
                  border: '2px solid white',
                  background: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              View More Projects
            </Button>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
};
